// src/components/WeatherForecastCard.ts
import "./WeatherForecastCard.css";
import {
  currentStoredLocationRaw,
  locationCoordinates,
  parseStoredUserLocation,
  requestFreshBrowserLocation,
  storedLocationIsFresh,
  locationDisplayLabel,
  freshStoredLocation,
  getFreshWeatherLocationContext,
} from "./UserLocation";

type FallbackRenderer = (
  container: HTMLElement,
  query: string,
) => void | Promise<void>;

export interface WeatherForecastCardOptions {
  locationOverride?: string;
  fallbackRenderer?: FallbackRenderer;
}

type AnyRecord = Record<string, any>;

const WEATHER_API_URL = "https://api.ayesearch.com/api/ai/weather/";
const NEAR_ME_RE = /\b(near me|my area|where i am|my location|locally)\b/i;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function numberOrNull(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function firstObject(...values: unknown[]): AnyRecord {
  for (const value of values) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as AnyRecord;
    }
  }
  return {};
}

function normalizeProviderPayload(raw: AnyRecord): AnyRecord {
  const candidates = [
    raw,
    raw?.weather,
    raw?.payload,
    raw?.data,
    raw?.result,
    raw?.cached_payload,
  ];

  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === "object" &&
      (candidate.current || candidate.forecast || candidate.location)
    ) {
      return candidate;
    }
  }

  return raw || {};
}

function extractError(raw: AnyRecord): string {
  const error = raw?.error;
  if (!error) return "";
  if (typeof error === "string") return error;
  return String(error?.message || error?.detail || "Weather service unavailable");
}

function weatherLocationFromQuery(query: string): string {
  const value = query.trim();

  if (NEAR_ME_RE.test(value)) return "";

  const patterns = [
    /^weather\s+in\s+(.+)$/i,
    /^weather\s+(.+)$/i,
    /^(.+?)\s+weather$/i,
    /^forecast\s+(?:for|in)\s+(.+)$/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]?.trim()) return match[1].trim();
  }

  if (/^weather$/i.test(value)) return "";
  return value;
}

function displayLocation(location: AnyRecord): string {
  const parts = [
    location?.name,
    location?.region,
    location?.country,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  const deduped = parts.filter(
    (part, index) =>
      parts.findIndex(
        (candidate) => candidate.toLowerCase() === part.toLowerCase(),
      ) === index,
  );

  return deduped.join(", ") || "Weather";
}

function shortLocation(location: AnyRecord): string {
  const name = String(location?.name || "").trim();
  const region = String(location?.region || "").trim();
  const country = String(location?.country || "").trim();

  if (name && region) return `${name}, ${region}`;
  if (name && country) return `${name}, ${country}`;
  return name || region || country || "your location";
}

function parseLocalDate(location: AnyRecord): Date {
  const localtime = String(location?.localtime || "").trim();
  if (localtime) {
    const normalized = localtime.includes("T")
      ? localtime
      : localtime.replace(" ", "T");
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

function formatLocalTime(location: AnyRecord): string {
  const date = parseLocalDate(location);
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function relativeUpdatedText(raw: AnyRecord, payload: AnyRecord): string {
  const candidates = [
    raw?.cache?.fetched_at,
    raw?.cache?.updated_at,
    raw?.fetched_at,
    raw?.updated_at,
    payload?.current?.last_updated,
  ];

  let date: Date | null = null;
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = new Date(String(candidate).replace(" ", "T"));
    if (!Number.isNaN(parsed.getTime())) {
      date = parsed;
      break;
    }
  }

  if (!date) return "Updated recently";

  const minutes = Math.max(
    0,
    Math.round((Date.now() - date.getTime()) / 60000),
  );

  if (minutes < 5) return "Updated a few minutes ago";
  if (minutes < 60) return `Updated ${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours === 1) return "Last updated 1 hour ago";
  return `Last updated ${hours} hours ago`;
}

function conditionIcon(condition: AnyRecord): string {
  const icon = String(condition?.icon || "").trim();
  if (!icon) return "";
  return icon.startsWith("//") ? `https:${icon}` : icon;
}

function formatHourLabel(time: unknown): string {
  const raw = String(time || "");
  const match = raw.match(/(\d{1,2}):(\d{2})$/);
  if (!match) return "";

  let hour = Number(match[1]);
  const suffix = hour >= 12 ? "PM" : "AM";
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour} ${suffix}`;
}

function dayLabel(dateValue: unknown, index: number): string {
  if (index === 0) return "Today";

  const parsed = new Date(`${String(dateValue || "")}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return `Day ${index + 1}`;

  return parsed.toLocaleDateString([], { weekday: "short" });
}

function selectHourlySlots(
  forecastDays: AnyRecord[],
  location: AnyRecord,
): AnyRecord[] {
  const allHours = forecastDays
    .flatMap((day) => (Array.isArray(day?.hour) ? day.hour : []))
    .filter(Boolean);

  if (!allHours.length) return [];

  const localNow = parseLocalDate(location).getTime();

  let startIndex = allHours.findIndex((hour) => {
    const time = new Date(String(hour?.time || "").replace(" ", "T")).getTime();
    return Number.isFinite(time) && time >= localNow - 30 * 60 * 1000;
  });

  if (startIndex < 0) startIndex = 0;

  const selected: AnyRecord[] = [];
  for (let i = startIndex; i < allHours.length && selected.length < 6; i += 3) {
    selected.push(allHours[i]);
  }

  if (selected.length < 6) {
    for (
      let i = startIndex + 1;
      i < allHours.length && selected.length < 6;
      i += 1
    ) {
      if (!selected.includes(allHours[i])) selected.push(allHours[i]);
    }
  }

  return selected.slice(0, 6);
}

function precipitationPercent(
  current: AnyRecord,
  hourly: AnyRecord[],
): number | null {
  const currentChance = numberOrNull(
    current?.chance_of_rain ?? current?.chance_of_precip,
  );
  if (currentChance !== null) return currentChance;

  for (const hour of hourly) {
    const chance = numberOrNull(
      hour?.chance_of_rain ?? hour?.chance_of_precip,
    );
    if (chance !== null) return chance;
  }

  return null;
}

function renderHourly(hourly: AnyRecord[], useFahrenheit: boolean): string {
  return hourly
    .map((hour) => {
      const temp = numberOrNull(useFahrenheit ? hour?.temp_f : hour?.temp_c);
      const icon = conditionIcon(firstObject(hour?.condition));
      const condition = String(hour?.condition?.text || "");

      return `
        <div class="aye-weather-hour">
          <span class="aye-weather-hour-time">${escapeHtml(formatHourLabel(hour?.time))}</span>
          ${
            icon
              ? `<img src="${escapeHtml(icon)}" alt="${escapeHtml(condition)}" loading="lazy" />`
              : `<div class="aye-weather-hour-icon-placeholder" aria-hidden="true"></div>`
          }
          <strong>${temp === null ? "—" : `${Math.round(temp)}°`}</strong>
        </div>
      `;
    })
    .join("");
}

function renderDays(days: AnyRecord[], useFahrenheit: boolean): string {
  return days
    .slice(0, 3)
    .map((forecastDay, index) => {
      const day = firstObject(forecastDay?.day);
      const high = numberOrNull(useFahrenheit ? day?.maxtemp_f : day?.maxtemp_c);
      const low = numberOrNull(useFahrenheit ? day?.mintemp_f : day?.mintemp_c);
      const icon = conditionIcon(firstObject(day?.condition));
      const condition = String(day?.condition?.text || "");
      const range =
        high === null || low === null
          ? "—"
          : `${Math.round(high)}° / ${Math.round(low)}°`;

      return `
        <div class="aye-weather-day">
          <strong>${escapeHtml(dayLabel(forecastDay?.date, index))}</strong>
          ${
            icon
              ? `<img src="${escapeHtml(icon)}" alt="${escapeHtml(condition)}" loading="lazy" />`
              : `<div class="aye-weather-day-icon-placeholder" aria-hidden="true"></div>`
          }
          <span>${escapeHtml(range)}</span>
        </div>
      `;
    })
    .join("");
}

async function fetchWeather(location: string): Promise<{
  raw: AnyRecord;
  provider: AnyRecord;
}> {
  const url = new URL(WEATHER_API_URL);
  url.searchParams.set("q", location);
  url.searchParams.set("days", "3");

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const raw = (await response.json()) as AnyRecord;
  const message = extractError(raw);

  if (!response.ok || message) {
    throw new Error(message || `Weather returned HTTP ${response.status}`);
  }

  const provider = normalizeProviderPayload(raw);
  if (!provider?.current || !provider?.location) {
    throw new Error("Incomplete weather response.");
  }

  return { raw, provider };
}

async function renderLocation(
  container: HTMLElement,
  query: string,
  location: string,
  options: WeatherForecastCardOptions,
  displayLocationOverride: string = "",
): Promise<void> {
  const { raw, provider } = await fetchWeather(location);
  renderCard(container, query, options, raw, provider, true, displayLocationOverride);
}

function renderCard(
  container: HTMLElement,
  query: string,
  options: WeatherForecastCardOptions,
  raw: AnyRecord,
  provider: AnyRecord,
  useFahrenheit: boolean,
  displayLocationOverride: string = "",
): void {
  const location = firstObject(provider?.location);
  const current = firstObject(provider?.current);
  const forecast = firstObject(provider?.forecast);
  const forecastDays = Array.isArray(forecast?.forecastday)
    ? forecast.forecastday
    : Array.isArray(forecast?.days)
      ? forecast.days
      : [];

  if (!forecastDays.length) {
    throw new Error("Forecast data was not returned.");
  }

  const hourly = selectHourlySlots(forecastDays, location);
  const currentTemp = numberOrNull(
    useFahrenheit ? current?.temp_f : current?.temp_c,
  );
  const currentIcon = conditionIcon(firstObject(current?.condition));
  const currentCondition = String(current?.condition?.text || "Weather");
  const humidity = numberOrNull(current?.humidity);
  const wind = numberOrNull(useFahrenheit ? current?.wind_mph : current?.wind_kph);
  const precip = precipitationPercent(current, hourly);

  container.innerHTML = `
    <div class="aye-weather-location-controls" aria-label="Weather location controls">
      <div class="aye-weather-for">
        Weather for <strong>${escapeHtml(displayLocationOverride || shortLocation(location))}</strong>
      </div>
      <div class="aye-weather-location-actions">
        <button type="button" class="aye-weather-location-button aye-weather-use-location">
          <span aria-hidden="true">◎</span>
          Use my location
        </button>
        <button type="button" class="aye-weather-change-location">
          Change location
        </button>
      </div>
    </div>

    <section class="aye-weather-forecast-card" aria-label="Weather forecast">
      <div class="aye-weather-header">
        <div>
          <h2>${escapeHtml(displayLocationOverride || displayLocation(location))}</h2>
          <div class="aye-weather-local-time">${escapeHtml(formatLocalTime(location))}</div>
          <div class="aye-weather-updated">${escapeHtml(relativeUpdatedText(raw, provider))}</div>
        </div>

        <div class="aye-weather-unit-toggle" aria-label="Temperature unit">
          <button type="button" data-weather-unit="f" class="${useFahrenheit ? "is-active" : ""}">°F</button>
          <span aria-hidden="true">|</span>
          <button type="button" data-weather-unit="c" class="${!useFahrenheit ? "is-active" : ""}">°C</button>
        </div>
      </div>

      <div class="aye-weather-current">
        ${
          currentIcon
            ? `<img class="aye-weather-current-icon" src="${escapeHtml(currentIcon)}" alt="${escapeHtml(currentCondition)}" />`
            : `<div class="aye-weather-current-icon aye-weather-icon-placeholder" aria-hidden="true"></div>`
        }

        <div class="aye-weather-current-copy">
          <div class="aye-weather-current-temp">
            ${currentTemp === null ? "—" : `${Math.round(currentTemp)}°`}
          </div>
          <div class="aye-weather-condition">${escapeHtml(currentCondition)}</div>
        </div>
      </div>

      <dl class="aye-weather-facts">
        <div>
          <dt>Precipitation</dt>
          <dd>${precip === null ? "—" : `${Math.round(precip)}%`}</dd>
        </div>
        <div>
          <dt>Humidity</dt>
          <dd>${humidity === null ? "—" : `${Math.round(humidity)}%`}</dd>
        </div>
        <div>
          <dt>Wind</dt>
          <dd>${
            wind === null
              ? "—"
              : `${wind.toFixed(1).replace(/\.0$/, "")} ${useFahrenheit ? "mph" : "km/h"}`
          }</dd>
        </div>
      </dl>

      <div class="aye-weather-hourly" aria-label="Hourly forecast">
        ${renderHourly(hourly, useFahrenheit)}
      </div>

      <div class="aye-weather-days" aria-label="Three day forecast">
        ${renderDays(forecastDays, useFahrenheit)}
      </div>

      <div class="aye-weather-source">
        Weather data from WeatherAPI.com · cached by AyeSearch
      </div>
    </section>
  `;

  container
    .querySelector<HTMLButtonElement>('[data-weather-unit="f"]')
    ?.addEventListener("click", () =>
      renderCard(container, query, options, raw, provider, true, displayLocationOverride),
    );

  container
    .querySelector<HTMLButtonElement>('[data-weather-unit="c"]')
    ?.addEventListener("click", () =>
      renderCard(container, query, options, raw, provider, false, displayLocationOverride),
    );

  container
    .querySelector<HTMLButtonElement>(".aye-weather-use-location")
    ?.addEventListener("click", async (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const original = button.innerHTML;
      button.disabled = true;
      button.textContent = "Finding location…";

      try {
        const fresh = await requestFreshBrowserLocation();
        await renderLocation(
          container,
          query,
          locationCoordinates(fresh),
          options,
          locationDisplayLabel(fresh),
        );
      } catch (error) {
        console.error("AyeSearch precise location refresh failed:", error);
        button.disabled = false;
        button.innerHTML = original;

        const geoError = error as GeolocationPositionError | Error;
        const code = "code" in geoError ? Number(geoError.code) : 0;
        if (code === 1) {
          window.alert(
            "Precise location is blocked for AyeSearch in this browser or device. Enable Location permission for this site, then tap “Use my location” again.",
          );
        }
      }
    });

  container
    .querySelector<HTMLButtonElement>(".aye-weather-change-location")
    ?.addEventListener("click", async () => {
      const next = window
        .prompt("Enter a city, state, or country:", shortLocation(location))
        ?.trim();

      if (!next) return;

      container.innerHTML = `<div class="aye-weather-loading">Loading weather…</div>`;

      try {
        await renderLocation(container, query, next, {
          ...options,
          locationOverride: next,
        });
      } catch (error) {
        console.error("AyeSearch changed-location forecast failed:", error);
        renderCard(container, query, options, raw, provider, useFahrenheit, displayLocationOverride);
      }
    });
}

export async function renderWeatherForecastCard(
  container: HTMLElement,
  query: string,
  options: WeatherForecastCardOptions = {},
): Promise<void> {
  container.innerHTML = `<div class="aye-weather-loading">Loading weather…</div>`;

  let location = options.locationOverride?.trim() || "";
  let displayLocationOverride = "";

  if (!location && !NEAR_ME_RE.test(query)) {
    location = weatherLocationFromQuery(query);
  }

  if (!location) {
    const storedLocation = freshStoredLocation();
    if (storedLocation) {
      location = locationCoordinates(storedLocation);
      displayLocationOverride = locationDisplayLabel(storedLocation);
    }

    if (!location) {
      // Prefer already-authorized precise GPS, then fall back to approximate
      // IP locality so a forecast is not blocked by geolocation permission.
      const context = await getFreshWeatherLocationContext();
      if (context) {
        location = context.coordinates;
        displayLocationOverride = context.displayLabel;
      }
    }
  }

  if (!location && !NEAR_ME_RE.test(query)) {
    const rawStored = currentStoredLocationRaw();
    const structured = parseStoredUserLocation(rawStored);

    if (structured && storedLocationIsFresh(structured)) {
      location = locationCoordinates(structured);
    } else if (rawStored && !rawStored.startsWith("{")) {
      location = rawStored;
    }
  }

  if (!location) {
    // Mobile/Safari/incognito can legitimately have no browser GPS context even
    // though the backend can still resolve an approximate city from the
    // originating request IP. Do not block the forecast in that case.
    //
    // The backend recognizes this sentinel and replaces it with the real client
    // IP (CF-Connecting-IP / X-Forwarded-For), then returns the normal 3-day
    // WeatherAPI payload. Precise GPS still wins whenever it is available.
    location = "__ayesearch_client_ip__";
    displayLocationOverride = "";
  }

  try {
    await renderLocation(container, query, location, options, displayLocationOverride);
  } catch (error) {
    console.error("AyeSearch forecast weather card failed:", error);

    if (options.fallbackRenderer) {
      await options.fallbackRenderer(container, location || query);
      return;
    }

    container.innerHTML = `
      <div class="aye-weather-loading aye-weather-warning">
        Forecast data is temporarily unavailable.
      </div>
    `;
  }
}
